import React, { memo } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { ExportType, ExportTypeOption } from '../../types/export';
import { choiceSurfaceSx } from '../selection/ChoiceCard';
import { EXPORT_TYPE_ICONS } from './exportTypeMeta';

const gridSx = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(180px, 100%), 1fr))', gap: 1.5 } as const;
const SKELETONS = Array.from({ length: 5 }, (_, i) => <Skeleton key={i} variant="rounded" height={120} sx={{ borderRadius: 3 }} />);

interface ExportTypeCardProps {
  type: ExportTypeOption;
  selected: boolean;
  onSelect: (value: ExportType) => void;
}

const ExportTypeCard: React.FC<ExportTypeCardProps> = ({ type, selected, onSelect }) => {
  const Icon = EXPORT_TYPE_ICONS[type.value];
  return (
    <ButtonBase
      role="radio"
      aria-checked={selected}
      onClick={() => onSelect(type.value)}
      sx={[
        choiceSurfaceSx(selected),
        {
          position: 'relative',
          minHeight: 120,
          // Compensate for the 2px selected border so the content doesn't shift.
          p: selected ? '15px' : 2,
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          gap: 1.5,
          textAlign: 'left',
        },
      ]}
    >
      <Box
        aria-hidden
        sx={{
          width: 40,
          height: 40,
          borderRadius: 3,
          display: 'grid',
          placeItems: 'center',
          color: selected ? 'brand.onContainer' : 'text.secondary',
          bgcolor: selected ? 'brand.container' : 'action.hover',
        }}
      >
        {Icon ? <Icon sx={{ fontSize: 22 }} /> : null}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pr: 3 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{type.label}</Typography>
        {type.description && <Typography sx={{ fontSize: 12, lineHeight: 1.4, color: 'text.secondary' }}>{type.description}</Typography>}
      </Box>
      {selected && <CheckCircle aria-hidden sx={{ position: 'absolute', top: 12, right: 12, fontSize: 20, color: 'primary.dark' }} />}
    </ButtonBase>
  );
};

interface ExportTypeGridProps {
  types: ExportTypeOption[];
  value: ExportType | '';
  loading: boolean;
  error: string | null;
  onChange: (value: ExportType) => void;
  onRetry: () => void;
}

const ExportTypeGrid: React.FC<ExportTypeGridProps> = ({ types, value, loading, error, onChange, onRetry }) => {
  if (loading) return <Box sx={gridSx}>{SKELETONS}</Box>;
  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        Couldn't load export types: {error}
      </Alert>
    );
  }
  return (
    <Box role="radiogroup" aria-label="What to export" sx={gridSx}>
      {types.map((type) => (
        <ExportTypeCard key={type.value} type={type} selected={type.value === value} onSelect={onChange} />
      ))}
    </Box>
  );
};

export default memo(ExportTypeGrid);
